import { verifyTypeMatches } from '../utils/verifyTypeMatches';

export type Validator<TValue = any> = (value: TValue, name: string) => undefined | string[] | string;
export type ValidatorFactory<TValue = any> = (...args: any[]) => Validator<TValue>;

/**
 * Combines multiple validators into one validator.
 * note: All validators are executed in the order defined
 */
const compose =
  <TValue>(...validators: Array<Validator<TValue>>) =>
  (value: any, name: string) => {
    return validators.flatMap(v => v(value, name)).filter((e): e is string => !!e);
  };

/**
 * Combines multiple validators into one validator but bails when the first validator fails
 * note: All validators are executed in the order defined
 */
const composeOrBail =
  <TValue>(...validators: Array<Validator<TValue>>) =>
  (value: any, name: string) => {
    for (const validator of validators) {
      const result = validator(value, name);
      if (result?.length) {
        return result;
      }
    }
  };

/**
 * Ignores a validator if the value has not been provided
 */
const ignoreIfUndefined =
  <TValue extends NonNullable<any>>(validator: Validator<TValue>): Validator<TValue | undefined> =>
  (value, name) =>
    isRequired()(value)?.length ? undefined : validator(value!, name);

/**
 * Validates that a value has been supplied
 */
const isRequired = (options?: { message?: string }) => (value: any) => {
  if (value !== 0 && value !== false && !(typeof value === 'string' ? value.trim() : value)) {
    return options?.message || 'Value is required';
  }
};

/**
 * Validates that a value matches a regular expression
 */
const matchesPattern = (options: { pattern: RegExp; message?: string }) => {
  return ignoreIfUndefined((value: string) => {
    if (!options.pattern.test(value)) {
      return options.message || 'Value is not in the correct format';
    }
  });
};

/**
 * Validates that a valid VIN has been provided
 */
const isValidVin = (options?: { allow16CharacterVin?: boolean }) =>
  matchesPattern({
    pattern: options?.allow16CharacterVin ? /^[a-z0-9]{16}.?$/i : /^[a-z0-9]{17}$/i,
    message: 'Invalid vin'
  });

/**
 * Validates that a value is numeric
 */
const isNumeric = (options?: { message?: string }) =>
  ignoreIfUndefined(value => {
    if (typeof value !== 'number' || isNaN(value) || value === Infinity) {
      return options?.message || 'Value must be numeric';
    }
  });

/**
 * Validates that a number is in a numeric range
 */
const isInRange = (options: { from: number; to: number; message?: string }) =>
  composeOrBail<number | undefined>(
    isNumeric({ message: options.message }),
    ignoreIfUndefined(value => {
      if (value < options.from || value > options.to) {
        return (
          options.message ||
          (options.from === Infinity
            ? `Value must be less than ${options.to.toLocaleString()}`
            : options.to === Infinity
            ? `Value must be greater than ${options.from.toLocaleString()}`
            : `Value must be between ${options.from.toLocaleString()} and ${options.to.toLocaleString()}`)
        );
      }
    })
  );

/**
 * Validates that a number is greater than a minimum value
 */
const isGreaterThan = (options: { maxValue: number; message?: string }) => {
  return isInRange({ from: options.maxValue, to: Infinity, message: options.message });
};

/**
 * Validates that a number is less than a minimum value
 */
const isLessThan = (options: { minValue: number; message?: string }) =>
  isInRange({ from: -Infinity, to: options.minValue, message: options.message });

/**
 * Validates that a number is an integer
 */
const isInteger = (options?: { message?: string }) =>
  ignoreIfUndefined(value => {
    if (Number(value) % 1 !== 0) {
      return options?.message || 'Value must be an integer';
    }
  });

/**
 * Validates that a number is a positive integer
 */
const isPositiveInteger = (options?: { message?: string }) =>
  composeOrBail(
    isInteger({ message: options?.message }),
    isInRange({ from: 0, to: Infinity, message: options?.message })
  );

export const validators = verifyTypeMatches<Record<string, Validator | ValidatorFactory>>()({
  compose,
  composeOrBail,
  isRequired,
  isInteger,
  isPositiveInteger,
  matchesPattern,
  isValidVin,
  isInRange,
  isNumeric,
  isLessThan,
  isGreaterThan
});
